import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { ATTACHMENT_MAX_COUNT, RequestStatus, type Attachment, type PostMessage, type RequestDetail, type UpdateRequest } from '@syndiqia/comms/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, errorMessage, personaHeader, useAuthHeaders } from '../api';
import { useCommsMutation, useMembers, useRequest } from '../comms-queries';
import { SuggestionPanel } from '../components/SuggestionPanel';
import { RequestWorkOrders } from './maintenance/RequestWorkOrders';
import { Alert, Loading } from '../components/ui';
import { useI18n } from '../i18n';
import { useParties, useUnits } from '../queries';
import { useSyndicate } from '../session';

export function RequestDetailPage() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const { requestId = '' } = useParams();
  const request = useRequest(requestId);
  const units = useUnits();
  const parties = useParties();
  const members = useMembers();
  const manages = s.can('requests.manage');
  const [draft, setDraft] = useState<string | null>(null);
  const update = useCommsMutation<UpdateRequest, RequestDetail>((api, base, input) => api(`${base}/requests/${requestId}`, { method: 'PATCH', body: input }));

  if (request.isPending) return <Loading label={t.loading} />;
  if (request.isError) return <Alert>{errorMessage(request.error, t)}</Alert>;
  const r = request.data;
  const statuses = manages ? t.requests.statusesBoard : t.requests.statuses;
  const board = members.data?.filter((m) => m.roles.some((role) => role === 'director' || role === 'manager')) ?? [];
  const time = (iso: string) => `${date(iso.slice(0, 10))} ${new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <>
      <Link to={`${s.base}/requests`} className="back">
        ← {t.requests.back}
      </Link>
      <header className="page-header">
        <div>
          <h1>
            #{r.number} — {r.subject}
          </h1>
          <p className="muted">
            {t.requests.categories[r.category]}
            {r.unitId && ` · ${t.requests.unit} ${units.data?.find((u) => u.id === r.unitId)?.label ?? ''}`}
            {manages && r.requesterPartyId && ` · ${parties.data?.find((p) => p.id === r.requesterPartyId)?.displayName ?? ''}`}
            {` · ${date(r.createdAt.slice(0, 10))}`}
          </p>
        </div>
        {!manages && <span className={`badge badge-status-${r.status}`}>{statuses[r.status]}</span>}
      </header>

      {manages && (
        <div className="toolbar request-controls">
          <label className="inline-field">
            {t.requests.status}
            <select value={r.status} onChange={(e) => update.mutate({ status: e.target.value as RequestDetail['status'] })}>
              {RequestStatus.options.map((st) => (
                <option key={st} value={st}>
                  {statuses[st]}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-field">
            {t.requests.assignee}
            <select value={r.assigneeUserId ?? ''} onChange={(e) => update.mutate({ assigneeUserId: e.target.value || null })}>
              <option value="">{t.requests.unassigned}</option>
              {board.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-field">
            <input type="checkbox" checked={r.priority === 'urgent'} onChange={(e) => update.mutate({ priority: e.target.checked ? 'urgent' : 'normal' })} />
            {t.requests.urgent}
          </label>
        </div>
      )}
      {update.isError && <Alert>{errorMessage(update.error, t)}</Alert>}

      <ol className="thread">
        {r.messages.map((m) => (
          <li key={m.id} className={`message${m.visibility === 'internal' ? ' message-internal' : ''}`}>
            <div className="message-meta">
              <strong>{m.authorName ?? '—'}</strong>
              <span className="muted">{time(m.createdAt)}</span>
              {m.visibility === 'internal' && <span className="badge badge-draft">{t.requests.internalBadge}</span>}
            </div>
            <p className="message-body">{m.body}</p>
            {m.attachments.length > 0 && <Attachments requestId={r.id} files={m.attachments} />}
            {m.deliveries.map((d) => (
              <div key={d.address} className="muted small">
                ✉ {t.requests.emailed(d.address)} — {t.requests.deliveryStatus[d.status]}
              </div>
            ))}
          </li>
        ))}
      </ol>

      {manages && r.status !== 'resolved' && <SuggestionPanel request={r} onUseDraft={setDraft} />}

      {s.can('maintenance.read') && <RequestWorkOrders request={r} />}

      <Reply requestId={r.id} manages={manages} draft={draft} />
    </>
  );
}

/** Files of a message: opened through the API with the session (no shareable link). */
function Attachments({ requestId, files }: { requestId: string; files: Attachment[] }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const headers = useAuthHeaders();
  const open = async (a: Attachment) => {
    const tab = window.open('', '_blank');
    const url = `${s.api}/comms/requests/${requestId}/attachments/${a.id}`;
    const res = await fetch(url, { headers: { ...headers(), ...personaHeader(url) } });
    if (!res.ok) return tab?.close();
    const blob = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = blob;
    else window.location.href = blob;
  };
  const size = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} ko` : `${(n / 1024 / 1024).toFixed(1)} Mo`);
  return (
    <ul className="attachments" aria-label={t.requests.attachments}>
      {files.map((a) => (
        <li key={a.id}>
          <button type="button" className="link-button small" onClick={() => void open(a)}>
            📎 {a.fileName}
          </button>
          <span className="muted small"> {size(a.sizeBytes)}</span>
        </li>
      ))}
    </ul>
  );
}

function Reply({ requestId, manages, draft }: { requestId: string; manages: boolean; draft: string | null }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const headers = useAuthHeaders();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [body, setBody] = useState('');
  useEffect(() => {
    if (draft) {
      setBody(draft);
      document.getElementById('reply-box')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [draft]);
  const [internal, setInternal] = useState(false);
  const post = useCommsMutation<PostMessage, RequestDetail>((api, base, input) => api(`${base}/requests/${requestId}/messages`, { method: 'POST', body: input }));
  // With files: one multipart request (the message and its files together).
  const postFiles = useMutation({
    mutationFn: async (input: PostMessage) => {
      const url = `${s.api}/comms/requests/${requestId}/messages-with-files`;
      const form = new FormData();
      form.append('body', input.body);
      form.append('internal', String(input.internal));
      for (const f of files) form.append('file', f);
      const res = await fetch(url, { method: 'POST', headers: { ...headers(), ...personaHeader(url) }, body: form });
      const json = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
      if (!res.ok) throw new ApiError(res.status, json);
      return json as RequestDetail;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'comms'] }),
  });
  const busy = post.isPending || postFiles.isPending;
  const error = post.error ?? postFiles.error;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim() && files.length === 0) return;
    const done = () => (setBody(''), setInternal(false), setFiles([]));
    if (files.length) postFiles.mutate({ body: body.trim(), internal }, { onSuccess: done });
    else post.mutate({ body: body.trim(), internal }, { onSuccess: done });
  };
  return (
    <form onSubmit={submit} className="panel form">
      <h2>{t.requests.reply}</h2>
      <textarea id="reply-box" rows={body.length > 300 ? 12 : 4} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t.requests.replyPlaceholder} />
      {manages && (
        <label className="inline-field">
          <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
          {t.requests.internal}
        </label>
      )}
      <label className="attach-files">
        <span className="link-button small">📎 {t.requests.attach}</span>
        <input
          type="file"
          multiple
          hidden
          accept="image/*,application/pdf"
          onChange={(e) => setFiles([...files, ...Array.from(e.target.files ?? [])].slice(0, ATTACHMENT_MAX_COUNT))}
        />
      </label>
      {files.length > 0 && (
        <ul className="attachments">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              📎 {f.name}{' '}
              <button type="button" className="link-button small" onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                {t.requests.removeFile}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <Alert>{errorMessage(error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={busy || (!body.trim() && files.length === 0)}>
          {t.requests.send}
        </button>
      </div>
    </form>
  );
}
